import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Loader2 } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { MobileCustomerPicker } from '../components/visit-report/MobileCustomerPicker';
import { VisitReportWizard } from '../components/visit-report/VisitReportWizard';
import { VisitReportDraftsBanner } from '../components/visit-report/VisitReportDraftsBanner';
import { visitReportsApi } from '../services/visitReportsApi';
import type { VisitReportCustomerOption as CustomerPortfolioItem } from '../types/visitReport';
import type { VisitReportSummary } from '../types/visitReport';

interface SelectedTarget {
    hubspotCompanyId: string;
    companyName: string | null;
    customerId?: string;
    companyCode?: string;
}

export function VisitReportPage() {
    const { draftId } = useParams<{ draftId?: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { canEdit, isLoading: permLoading } = usePagePermissions('/visit-report');

    const [target, setTarget] = useState<SelectedTarget | null>(null);
    const [drafts, setDrafts] = useState<VisitReportSummary[]>([]);
    const [loadingInitialDraft, setLoadingInitialDraft] = useState(false);

    // If we're opened with /visit-report/:draftId, pre-fill target from the draft.
    useEffect(() => {
        if (!draftId) return;
        setLoadingInitialDraft(true);
        visitReportsApi.getDraft(draftId)
            .then(record => {
                setTarget({
                    hubspotCompanyId: record.hubspotCompanyId,
                    companyName: record.companyName,
                    customerId: record.customerId ?? undefined,
                    companyCode: record.companyCode ?? undefined,
                });
            })
            .catch(err => {
                console.error('Failed to load draft:', err);
                navigate('/visit-report', { replace: true });
            })
            .finally(() => setLoadingInitialDraft(false));
    }, [draftId, navigate]);

    // If we're opened with /visit-report?hubspotCompanyId=… (deep-link from
    // Customer 360), pre-fill the target so the rep skips the customer picker.
    useEffect(() => {
        if (draftId) return;
        const hubspotCompanyId = searchParams.get('hubspotCompanyId');
        if (!hubspotCompanyId) return;
        setTarget(prev => prev ?? {
            hubspotCompanyId,
            companyName: searchParams.get('companyName'),
            customerId: searchParams.get('customerId') ?? undefined,
            companyCode: searchParams.get('companyCode') ?? undefined,
        });
    }, [draftId, searchParams]);

    // Load recent drafts for the current user on initial render (no target yet)
    useEffect(() => {
        if (target || draftId) return;
        visitReportsApi.listDrafts('draft').then(setDrafts).catch(() => { /* ignore */ });
    }, [target, draftId]);

    if (permLoading) {
        return <div className="p-6 text-gray-400">Loading …</div>;
    }
    if (!canEdit) {
        return <div className="p-6 text-red-500">You do not have permission to create visit reports.</div>;
    }

    const handleCustomerSelect = (c: CustomerPortfolioItem) => {
        if (!c.hubspotCompanyId) {
            // Without a HubSpot link we cannot sync — block selection.
            alert('This customer is not linked to HubSpot. Please select another customer or link in HubSpot first.');
            return;
        }
        setTarget({
            hubspotCompanyId: c.hubspotCompanyId,
            companyName: c.customerName,
            customerId: c.customerId,
            companyCode: c.companyCode,
        });
    };

    const handleChangeCustomer = () => {
        setTarget(null);
        navigate('/visit-report', { replace: true });
    };

    const handleResumeDraft = (id: string) => {
        navigate(`/visit-report/${id}`);
    };

    if (loadingInitialDraft) {
        return (
            <div className="p-6 flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading draft …
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto space-y-4 pb-24 min-w-0">
            {/* Mobile-friendly header */}
            <div className="flex items-center gap-3">
                {target && (
                    <button
                        type="button"
                        onClick={handleChangeCustomer}
                        className="p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-blue-600" />
                        <h1 className="text-xl font-bold text-gray-900 truncate">Visit Report</h1>
                    </div>
                    <p className="text-xs text-gray-500">
                        {target
                            ? `Capturing visit with ${target.companyName || 'customer'}`
                            : 'Select a customer to start a new visit report'}
                    </p>
                </div>
            </div>

            {!target && (
                <VisitReportDraftsBanner
                    drafts={drafts}
                    onResume={handleResumeDraft}
                    onDraftsChange={setDrafts}
                />
            )}

            {!target && <MobileCustomerPicker onSelect={handleCustomerSelect} />}

            {target && (
                <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5">
                    <VisitReportWizard
                        hubspotCompanyId={target.hubspotCompanyId}
                        companyName={target.companyName}
                        customerId={target.customerId}
                        companyCode={target.companyCode}
                        initialDraftId={draftId}
                    />
                </div>
            )}
        </div>
    );
}
